import { useEffect, useState, useRef } from "react";
import { useReviewKeyboardShortcuts } from "@/lib/keyboardShortcuts";
import { ReviewSessionSkeleton } from "@/components/skeletons";
import { bigCelebrate } from "@/lib/confetti";
import { Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { postOrQueue } from "@/lib/offlineSync";
import OfflineSyncBanner from "@/components/OfflineSyncBanner";
import ExitConfirmDialog from "@/components/ExitConfirmDialog";
import { useExitGuard } from "@/hooks/useExitGuard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, CheckCircle2, XCircle, Sparkles, ArrowRight, Loader2, Trophy } from "lucide-react";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildDisplayChoices(q) {
  const originals = [
    { original: "a", text: q.choice_a },
    { original: "b", text: q.choice_b },
    { original: "c", text: q.choice_c },
    { original: "d", text: q.choice_d },
  ].filter((c) => c.text && c.text.trim() !== "");
  return shuffle(originals).map((c, i) => ({ ...c, display: String.fromCharCode(65 + i) }));
}

export default function SmartReviewSession() {
  const nav = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState({ c: 0, t: 0 });
  const [aiOpen, setAiOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [done, setDone] = useState(null);
  // FSRS+ Confidence-weighted rating UI (opt-in via Account → Study Plan)
  const [confidenceUI, setConfidenceUI] = useState(false);

  useEffect(() => {
    api.get("/me/study-prefs")
      .then((r) => setConfidenceUI(!!r.data?.confidence_rating_ui))
      .catch(() => {});
  }, []);

  // Exit confirmation guard — active during an in-progress Smart Review session
  const inProgress = !done && questions.length > 0;
  const { confirmOpen, confirmExit, cancelExit } = useExitGuard(inProgress);

  useEffect(() => {
    const raw = sessionStorage.getItem("pm_smart_session");
    if (!raw) { nav("/student/smart-review"); return; }
    const sess = JSON.parse(raw);
    setSessionId(sess.session_id);
    const builtQs = sess.questions.map((q) => ({ ...q, displayChoices: buildDisplayChoices(q) }));

    // Phase 47 — Smart Resume: if SmartReviewPage stashed resume-state into
    // sessionStorage (set when Resume button was clicked), honor it: jump to
    // the saved idx and pre-populate the score counter from answered IDs.
    const resumeIdxRaw = sessionStorage.getItem("pm_smart_resume_idx");
    const answeredRaw = sessionStorage.getItem("pm_smart_resume_answered");
    if (resumeIdxRaw && answeredRaw) {
      const resumeIdx = Math.min(Math.max(0, parseInt(resumeIdxRaw, 10) || 0), builtQs.length - 1);
      try {
        const answeredIds = JSON.parse(answeredRaw) || [];
        if (answeredIds.length > 0 || resumeIdx > 0) {
          setIdx(resumeIdx);
          setScore({ c: 0, t: answeredIds.length });
          toast.success(`Welcome back — resuming at question ${resumeIdx + 1}`, {
            duration: 4500,
            description: "Your progress was saved.",
          });
        }
      } catch { /* ignore parse errors */ }
      sessionStorage.removeItem("pm_smart_resume_idx");
      sessionStorage.removeItem("pm_smart_resume_answered");
    }
    setQuestions(builtQs);
  }, [nav]);

  const current = questions[idx];

  // FSRS time-to-answer tracker — informs Easy/Good/Hard rating for Smart Review.
  const shownAtRef = useRef(Date.now());
  useEffect(() => { shownAtRef.current = Date.now(); }, [idx, questions.length]);

  // Phase 47 — Smart Resume: debounce-save current_idx to server on change.
  // Skips before sessionId is loaded so we don't fire a save against undefined.
  useEffect(() => {
    if (!sessionId) return;
    const t = setTimeout(() => {
      api.patch(`/student/smart-review/${sessionId}/progress`, { current_idx: idx })
        .catch(() => { /* silent retry on next change */ });
    }, 500);
    return () => clearTimeout(t);
  }, [idx, sessionId]);

  // Phase 47 — Final flush on tab close via keepalive fetch (sendBeacon can't
  // attach Authorization headers, fetch keepalive can — and both survive
  // page-unload).
  useEffect(() => {
    if (!sessionId) return;
    const flush = () => {
      try {
        const tok = localStorage.getItem("token");
        if (!tok) return;
        fetch(`${API}/student/smart-review/${sessionId}/progress`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
          body: JSON.stringify({ current_idx: idx }),
          keepalive: true,
        });
      } catch { /* best-effort only */ }
    };
    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("pagehide", flush);
    };
  }, [sessionId, idx]);

  const submit = async (confidenceLabel = null) => {
    if (!selected || !current) return;
    setSubmitting(true);
    try {
      const r = await postOrQueue(api, "/student/smart-review/answer", {
        session_id: sessionId,
        question_id: current.id,
        selected_answer: selected.original.toUpperCase(),
        selected_display_label: selected.display,
        selected_choice_text: selected.text,
        time_taken_ms: Math.max(0, Date.now() - shownAtRef.current),
        confidence_label: confidenceLabel,
      });
      if (r.queued) {
        toast.info("Saved offline — will sync when you reconnect", { duration: 4000 });
        // Optimistically advance to next question without showing feedback
        setResult(null);
        setScore((s) => ({ c: s.c, t: s.t + 1 }));
        // Skip directly to next
        if (idx + 1 >= questions.length) {
          toast.info("Session paused — finish online to complete", { duration: 6000 });
        } else {
          setIdx((i) => i + 1); setSelected(null); setAiOpen(false);
        }
        return;
      }
      if (!r.ok) { toast.error(r.error?.response?.data?.detail || "Failed"); return; }
      const correctDisplay = current.displayChoices.find((c) => c.original === r.data.correct_answer.toLowerCase());
      setResult({ ...r.data, correctDisplay });
      setScore((s) => ({ c: s.c + (r.data.is_correct ? 1 : 0), t: s.t + 1 }));
      if (r.data.new_badges && r.data.new_badges.length > 0) {
        r.data.new_badges.forEach((b) =>
          toast.success(`🏆 Badge unlocked: ${b.name}`, { description: b.description, duration: 5000 })
        );
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const next = async () => {
    if (idx + 1 >= questions.length) {
      setFinishing(true);
      try {
        const r = await api.post(`/student/smart-review/${sessionId}/complete`);
        setDone(r.data);
        sessionStorage.removeItem("pm_smart_session");
      } catch (e) { toast.error("Could not finish session"); }
      finally { setFinishing(false); }
      return;
    }
    setIdx((i) => i + 1); setSelected(null); setResult(null); setAiOpen(false);
  };

  // FSRS+ Wave A — keyboard shortcuts (1-4/A-D, Space=submit/next, R=rationale).
  useReviewKeyboardShortcuts({
    enabled: !done,
    letters: current?.displayChoices?.map((c) => c.display) || [],
    onPick: (display) => {
      const c = current?.displayChoices?.find((x) => x.display === display);
      if (c) setSelected({ display: c.display, original: c.original, text: c.text });
    },
    onSubmit: () => {
      if (!result && selected && !submitting) submit();
    },
    onNext: () => {
      if (result) next();
    },
    hasResult: !!result,
    onToggleRationale: () => setAiOpen((v) => !v),
  });

  if (done) {
    const pct = done.accuracy;
    // Celebrate strong completions (≥80% → big confetti; 60–80% → small pride emoji)
    if (pct >= 80 && !done._celebrated) {
      done._celebrated = true;
      try { bigCelebrate(); } catch { /* confetti is non-critical */ }
    }
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="smart-review-complete">
        <Card className="p-10 border-slate-200 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-indigo-100 grid place-items-center text-indigo-700 mb-4">
            <Trophy className="w-7 h-7" />
          </div>
          <div className="font-display text-5xl font-black text-blue-700">{pct}%</div>
          <h2 className="font-display text-2xl font-bold mt-3">Smart Review Complete!</h2>
          <p className="text-slate-600 mt-2">You got {done.correct} of {done.total} correct.</p>
          {done.insight?.student_summary && (
            <div className="mt-6 p-4 rounded-md ai-card text-left">
              <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-1 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> AI Learning Insight</div>
              <p className="text-sm text-slate-800 leading-relaxed">{done.insight.student_summary}</p>
              {done.insight.recommended_action && (
                <p className="text-sm text-slate-700 mt-2"><span className="font-semibold">Next step:</span> {done.insight.recommended_action}</p>
              )}
            </div>
          )}
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/student/smart-review"><Button variant="outline">Back to Smart Review</Button></Link>
            <Link to="/student"><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Dashboard</Button></Link>
          </div>
        </Card>
      </div>
    );
  }

  if (!current) return <ReviewSessionSkeleton />;

  const renderText = (text, keyword) => {
    if (!keyword || !text?.toLowerCase().includes(keyword.toLowerCase())) return text;
    const i = text.toLowerCase().indexOf(keyword.toLowerCase());
    return (<>{text.slice(0, i)}<span className="keyword-highlight">{text.slice(i, i + keyword.length)}</span>{text.slice(i + keyword.length)}</>);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="smart-review-session">
      <Link to="/student/smart-review" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Exit</Link>
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Smart Review · Q {idx + 1} of {questions.length}</div>
        <div className="text-sm text-slate-500">Score: {score.c}/{score.t}</div>
      </div>
      <Progress value={(idx / questions.length) * 100} className="h-2" />

      <OfflineSyncBanner />

      <Card className="p-8 border-slate-200">
        {current.priority_reasons?.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1.5">
            {current.priority_reasons.map((r) => <Badge key={r} variant="outline" className="text-xs">{r}</Badge>)}
          </div>
        )}
        <p className="text-xl leading-relaxed text-slate-900 font-medium">{renderText(current.question_text, current.keyword)}</p>
        <div className="mt-6 space-y-3">
          {current.displayChoices.map((c) => {
            const isSelected = selected?.original === c.original;
            const isCorrect = result && result.correct_answer && result.correct_answer.toLowerCase() === c.original;
            const isWrongSelection = result && isSelected && !result.is_correct;
            return (
              <button
                key={c.original}
                disabled={!!result}
                onClick={() => setSelected({ display: c.display, original: c.original, text: c.text })}
                data-testid={`choice-${c.display}`}
                className={`w-full text-left p-4 rounded-md border transition-all duration-300 active:scale-[0.99] ${
                  isCorrect ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 ring-2 ring-emerald-300 animate-pulse-emerald" :
                  isWrongSelection ? "border-red-400 bg-red-50 dark:bg-red-900/30 opacity-90" :
                  isSelected ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-200 dark:ring-blue-800" :
                  result ? "border-slate-200 bg-white dark:bg-slate-900 opacity-40 grayscale" :
                  "border-slate-200 hover:border-slate-400 hover:shadow-sm bg-white dark:bg-slate-900 cursor-pointer"
                }`}
              >
                <span className="font-bold mr-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-slate-700 text-xs tabular-nums">{c.display}</span>
                <span>{c.text}</span>
              </button>
            );
          })}
        </div>

        {!result ? (
          confidenceUI ? (
            <div className="mt-6 space-y-2" data-testid="confidence-rating-row">
              <p className="text-[12px] text-slate-500 text-center mb-1">
                Tap how confident you are — used to schedule the next review.
              </p>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  className="h-12 flex-col gap-0.5 border-amber-300 text-amber-800 hover:bg-amber-50"
                  disabled={!selected || submitting}
                  onClick={() => submit("guessed")}
                  data-testid="confidence-guessed-btn"
                >
                  <span className="text-base leading-none">😬</span>
                  <span className="text-[11px] font-semibold">Guessed</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-12 flex-col gap-0.5 border-blue-300 text-blue-800 hover:bg-blue-50"
                  disabled={!selected || submitting}
                  onClick={() => submit("knew")}
                  data-testid="confidence-knew-btn"
                >
                  <span className="text-base leading-none">🤔</span>
                  <span className="text-[11px] font-semibold">Knew it</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-12 flex-col gap-0.5 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                  disabled={!selected || submitting}
                  onClick={() => submit("obvious")}
                  data-testid="confidence-obvious-btn"
                >
                  <span className="text-base leading-none">💯</span>
                  <span className="text-[11px] font-semibold">Obvious</span>
                </Button>
              </div>
              {submitting && (
                <div className="flex justify-center pt-1"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
              )}
            </div>
          ) : (
            <Button className="mt-6 w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" disabled={!selected || submitting} onClick={() => submit()} data-testid="submit-answer-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Answer"}
            </Button>
          )
        ) : (
          <>
            <div className={`mt-6 p-4 rounded-md border animate-slide-up-fade ${result.is_correct ? "bg-green-50 border-green-200 text-green-800 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-200" : "bg-red-50 border-red-200 text-red-800 dark:bg-rose-900/30 dark:border-rose-700 dark:text-rose-200"}`}>
              <div className="flex items-center gap-2 font-bold">
                {result.is_correct ? <><CheckCircle2 className="w-5 h-5" /> Correct!</> : <><XCircle className="w-5 h-5" /> Incorrect</>}
              </div>
              {!result.is_correct && result.correctDisplay && (
                <div className="mt-2 text-sm">
                  <span className="text-slate-600">Correct answer:</span>{" "}
                  <span className="font-semibold text-slate-900">{result.correctDisplay.display}. {result.correctDisplay.text}</span>
                </div>
              )}
            </div>

            <Button className="mt-6 w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" disabled={finishing} onClick={next} data-testid="next-question-btn">
              {finishing ? <Loader2 className="w-4 h-4 animate-spin" /> : (idx + 1 >= questions.length ? "Finish Smart Review" : <>Next Question <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>

            <Button variant="outline" className="mt-3 w-full h-11 border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => setAiOpen(true)} data-testid="explain-with-ai-btn">
              <Sparkles className="w-4 h-4 mr-2" /> Explain with AI
            </Button>
          </>
        )}
      </Card>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="w-5 h-5 text-indigo-600" /> Rationale</DialogTitle>
            <DialogDescription>Review the explanation for this question.</DialogDescription>
          </DialogHeader>
          {result && !result.explanation ? (
            <div className="py-8 text-center"><p className="text-sm text-slate-600">No rationale is available for this question yet.</p></div>
          ) : result && result.explanation && result.correctDisplay ? (
            <div className="space-y-4">
              <div className="ai-card p-4">
                <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Correct Answer</div>
                <div className="text-sm font-semibold text-slate-900 mt-1 mb-3">{result.correctDisplay.display}. {result.correctDisplay.text}</div>
                <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-1">Why it is correct</div>
                <p className="text-sm leading-relaxed text-slate-700">{result.explanation.correct_answer_explanation}</p>
              </div>
              {!result.is_correct && selected && result.explanation[`choice_${selected.original}_explanation`] && (
                <div className="p-4 rounded-md border border-slate-200 bg-slate-50">
                  <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">Your Answer</div>
                  <div className="text-sm font-semibold text-slate-900 mt-1 mb-3">{selected.display}. {selected.text}</div>
                  <div className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Why it is incorrect</div>
                  <p className="text-sm leading-relaxed text-slate-700">{result.explanation[`choice_${selected.original}_explanation`]}</p>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter><Button variant="outline" onClick={() => setAiOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ExitConfirmDialog
        open={confirmOpen}
        onCancel={cancelExit}
        onConfirm={confirmExit}
        mode="smart-review"
      />
    </div>
  );
}
